import { Box, type BoxProps, Card, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlanAllowance, SubscriptionPlanConfig } from '@/api';
import { formatEur, formatNumber, vfiatToEur } from '@/lib/money';
import { planLabel, USAGE_SERVICES } from '@/pages/Account/Billing/billing-labels';
import { useRustyState } from '../../state';

const NAVY = '#0A192F';

const PLAN_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 24,
  alignItems: 'stretch',
} as const;

const HEADING_STYLE = { letterSpacing: '0.04em' } as const;

// Plan feature ids from `backend/formshive/src/rates.rs` mapped to the integration adapter names.
const FEATURE_ADAPTERS: Record<string, string> = {
  'integration:webhook': 'generic_webhook',
  'integration:webhook_zapier': 'zapier_webhook',
  'integration:webhook_slack': 'slack_url',
  'integration:webhook_google_sheets': 'google_sheets_webhook',
  'integration:api_pipedrive': 'api_pipedrive',
  'integration:api_mailchimp': 'api_mailchimp',
  'integration:api_kit': 'api_kit',
};

function allowanceLine(allowance: PlanAllowance, t: TFunction, lang: string) {
  const service = USAGE_SERVICES[allowance.service];
  if (service?.unit === 'kb') {
    return allowance.limit < 0
      ? t('billing.allowanceFilesUnlimited')
      : t('billing.allowanceFiles', { mb: formatNumber(allowance.limit / 1024, lang) });
  }
  const what = service ? t(service.nounKey) : allowance.service.replace(/_/g, ' ');
  return allowance.limit < 0
    ? t('billing.allowanceUnlimited', { what })
    : t('billing.allowanceCount', {
        count: allowance.limit,
        n: formatNumber(allowance.limit, lang),
        what,
      });
}

function featureLabel(feature: string, t: TFunction) {
  const adapter = FEATURE_ADAPTERS[feature];
  return adapter ? t(`integrations.adapter.${adapter}.name`) : feature.replace(/^integration:/, '');
}

interface CheckItemProps {
  children: React.ReactNode;
  highlight?: boolean;
}

function CheckItem({ children, highlight }: CheckItemProps) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <ThemeIcon
        size={18}
        radius="xl"
        variant="light"
        color={highlight ? 'dark' : 'gray'}
        style={{ marginTop: 2, flexShrink: 0 }}
      >
        <IconCheck size={12} />
      </ThemeIcon>
      <Text size="sm" lh={1.5}>
        {children}
      </Text>
    </Group>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={HEADING_STYLE}>
      {children}
    </Text>
  );
}

/** The public plan cards on the pricing page. */
export function SubscriptionPlans(boxProps: BoxProps) {
  const { t, i18n } = useTranslation();
  const { subscriptionPlans, getAndSetSubscriptionPlans } = useRustyState();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const lang = i18n.language;

  useEffect(() => {
    getAndSetSubscriptionPlans()
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [getAndSetSubscriptionPlans]);

  if (loading) {
    return (
      <Box style={PLAN_GRID_STYLE} {...boxProps}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} withBorder radius="md" p="xl">
            <Stack gap="md">
              <Skeleton height={24} width={100} />
              <Skeleton height={40} width={120} />
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="80%" />
              <Skeleton height={14} width="90%" />
            </Stack>
          </Card>
        ))}
      </Box>
    );
  }

  const plans = [...(subscriptionPlans ?? [])].sort((a, b) => a.price - b.price);

  if (failed || plans.length === 0) {
    return (
      <Text c="dimmed" ta="center" {...boxProps}>
        {t('billing.plans.none')}
      </Text>
    );
  }

  // The middle plan of three, otherwise the third, is marked "Most popular".
  const popularIndex = plans.length >= 3 ? (plans.length === 3 ? 1 : 2) : -1;

  return (
    <Box style={PLAN_GRID_STYLE} {...boxProps}>
      {plans.map((plan: SubscriptionPlanConfig, index: number) => {
        const isPopular = index === popularIndex;

        return (
          <Card
            key={plan.id}
            withBorder
            radius="md"
            p="xl"
            style={{
              position: 'relative',
              border: isPopular ? `1.5px solid ${NAVY}` : undefined,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              marginTop: isPopular ? 12 : 0,
            }}
          >
            {isPopular && (
              <Box
                style={{
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#FFB800',
                  color: NAVY,
                  border: `1.5px solid ${NAVY}`,
                  borderRadius: 9999,
                  padding: '4px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}
              >
                {t('billing.pricing.popular')}
              </Box>
            )}

            <Stack gap="md" style={{ flexGrow: 1 }}>
              <Text fw={600} size="lg">
                {plan.is_free_plan ? t('billing.pricing.free') : planLabel(plan.name)}
              </Text>

              <Group gap={4} align="baseline">
                <Text size="2.25rem" fw={700} lh={1}>
                  {formatEur(vfiatToEur(plan.price), lang, 0, 2)}
                </Text>
                {plan.price > 0 && (
                  <Text size="sm" c="dimmed">
                    {t('billing.plans.perMonth')}
                  </Text>
                )}
              </Group>

              <Stack gap={6}>
                <Heading>{t('billing.pricing.included')}</Heading>
                {plan.allowances.map((allowance) => (
                  <CheckItem key={allowance.service} highlight={isPopular}>
                    {allowanceLine(allowance, t, lang)}
                  </CheckItem>
                ))}
              </Stack>

              <Stack gap={6}>
                <Heading>{t('billing.pricing.retentionTitle')}</Heading>
                <CheckItem highlight={isPopular}>
                  {plan.max_retention_days
                    ? t('billing.plans.retention', { days: plan.max_retention_days })
                    : t('billing.pricing.retentionUnlimited')}
                </CheckItem>
              </Stack>

              {plan.features.length > 0 && (
                <Stack gap={6}>
                  <Heading>{t('billing.pricing.integrationsTitle')}</Heading>
                  {plan.features.map((feature) => (
                    <CheckItem key={feature} highlight={isPopular}>
                      {featureLabel(feature, t)}
                    </CheckItem>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        );
      })}
    </Box>
  );
}
